import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  IconButton,
  Divider,
  Paper,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { firebaseShippingService } from '../services/firebaseShippingService';
import { formatWeightKg, SHIPPING_ZONE_LABELS } from '../utils/shippingUtils';
import { getWineCategoryLabel } from '../utils/wineUtils';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateCartQuantity, clearCart } = useStore();
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  useEffect(() => {
    if (cart.length === 0) {
      setShippingEstimate(null);
      return;
    }

    let cancelled = false;
    setShippingLoading(true);

    firebaseShippingService
      .calculateForCart(cart, { zone: 'peninsula' })
      .then((result) => {
        if (!cancelled) setShippingEstimate(result);
      })
      .catch(() => {
        if (!cancelled) setShippingEstimate(null);
      })
      .finally(() => {
        if (!cancelled) setShippingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cart]);

  const shippingCost = shippingEstimate?.cost ?? 0;
  const calculateTotal = () => calculateSubtotal() + shippingCost;

  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item.id);
    } else if (newQuantity <= item.stock) {
      updateCartQuantity(item.id, newQuantity);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Box sx={{ mb: 4 }}>
          <CartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, color: 'text.secondary' }}>
            Tu carrito está vacío
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Descubre nuestra selección de vinos y agrega algunos a tu carrito
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon />}
          >
            Continuar comprando
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontFamily: '"Playfair Display", serif' }}>
        Mi Carrito
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {cart.map((item) => (
            <Card key={item.id} sx={{ mb: 2, position: 'relative' }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3} sm={2}>
                    <CardMedia
                      component="img"
                      image={item.imageUrl || '/images/wine-placeholder.jpg'}
                      alt={item.name}
                      sx={{
                        height: 80,
                        width: 60,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={5}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      {getWineCategoryLabel(item) && (
                        <Chip
                          label={getWineCategoryLabel(item)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {item.type && (
                        <Chip label={item.type} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(item.price)} c/u
                    </Typography>
                  </Grid>

                  <Grid item xs={3} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        <RemoveIcon />
                      </IconButton>

                      <Typography
                        variant="body1"
                        sx={{
                          mx: 2,
                          minWidth: 30,
                          textAlign: 'center',
                          fontWeight: 600
                        }}
                      >
                        {item.quantity}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        disabled={item.quantity >= item.stock}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>

                    {item.quantity >= item.stock && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                      >
                        Stock máximo
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                        {formatPrice(item.price * item.quantity)}
                      </Typography>
                      <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ mr: 2 }}
            >
              Continuar comprando
            </Button>

            <Button variant="outlined" color="error" onClick={clearCart}>
              Vaciar carrito
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Resumen del pedido
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Subtotal ({cart.reduce((total, item) => total + item.quantity, 0)} productos)
                </Typography>
                <Typography variant="body2">{formatPrice(calculateSubtotal())}</Typography>
              </Box>

              {shippingEstimate?.weightKg != null && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Peso estimado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatWeightKg(shippingEstimate.weightKg)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Envío</Typography>
                {shippingLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography
                    variant="body2"
                    color={shippingCost === 0 ? 'success.main' : 'text.primary'}
                  >
                    {shippingCost === 0 ? 'Gratis' : formatPrice(shippingCost)}
                  </Typography>
                )}
              </Box>

              {shippingEstimate && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Estimación para {SHIPPING_ZONE_LABELS.peninsula}
                  {shippingEstimate.bracketLabel ? ` · ${shippingEstimate.bracketLabel}` : ''}
                </Typography>
              )}

              {shippingEstimate?.freeShippingThreshold &&
                !shippingEstimate.freeShipping &&
                calculateSubtotal() < shippingEstimate.freeShippingThreshold && (
                  <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                    Añade{' '}
                    {formatPrice(shippingEstimate.freeShippingThreshold - calculateSubtotal())} más para
                    envío gratis
                  </Alert>
                )}

              <Alert severity="info" sx={{ mt: 1 }}>
                El envío se calcula por código postal y peso (1 kg/botella): Huelva urbano,
                Andalucía regional y resto península. Más de 10 kg aplica suplemento por kg.
              </Alert>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total estimado
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {shippingLoading ? '...' : formatPrice(calculateTotal())}
              </Typography>
            </Box>

            <Button variant="contained" fullWidth size="large" onClick={handleCheckout} sx={{ mb: 2 }}>
              Proceder al pago
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Envío seguro y garantía de calidad
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Cart;
